import type { TextAlignment } from "building-types/annotation/annotationTypesEnum";
import type { AggregationMethod } from "building-types/dataSequences/measures";
import type { SystemDataSequenceId } from "building-types/dataSequences/systemDataSequenceTypes";
import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { DocEntityId } from "building-types/doc/docEntityId";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { FormulaDisabled } from "building-types/options/formulaDisabled";
import type { ParameterRef } from "building-types/options/parameter";
import type { OptionPresetBase } from "building-types/views/optionPresetTypes";
import type { Table } from "common/data/table";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import type { ForeignEntityId } from "../handles/foreignEntityId";

export interface DataTableColumnWidths {
  tableWidth: SheetInches;
  columnOrder: string[];
  columnWidths: { [key: string]: SheetInches | undefined };
}

export interface DataTableFindAndReplaceRow {
  readonly index?: number;
  readonly columnId: string;
  readonly find: string;
  readonly replace: string;
}

export const DATA_TABLE_COLUMN_SORT_BY = ["ASCENDING", "DESCENDING"];
export type DataTableColumnSortOrder = (typeof DATA_TABLE_COLUMN_SORT_BY)[number];

export interface DataTableColumnConfig {
  readonly index?: number;
  readonly isHidden?: boolean;
  readonly width?: number;
  readonly displayName?: string;
  readonly includeFilter?: string;
  readonly excludeFilter?: string;
  readonly aggregationMethod?: AggregationMethod;
}

export type DataTableTitleConfig = Pick<DataTable, "title" | "titleHidden" | "titleFontSize" | "titleAlignment">;

export interface DataTable extends OptionPresetBase, DocEntityBase<"DATA_TABLE">, FormulaDisabled {
  readonly id: PrimaryEntityId<"DATA_TABLE">;
  readonly parentId?: DocEntityParentId<"CONTENT_TEMPLATE" | "CONTENT_TEMPLATE_INSTANCE">;
  readonly dataSequenceId: ParameterRef | DocEntityId<"DATA_SEQUENCE"> | SystemDataSequenceId;
  readonly levelId?: ParameterRef | DocEntityId<"LEVEL">;
  readonly selectedReportingUnitLabelId?: ParameterRef | ForeignEntityId<"LABEL">;
  readonly fontSize?: SheetInches;
  readonly x: SheetInches;
  readonly y: SheetInches;
  readonly width: SheetInches;
  readonly height: SheetInches; // tables overflow from the bottom today, so this is really more of a UI guideline
  readonly columnHeadersHidden?: boolean;
  readonly titleHidden?: boolean;
  readonly title?: string;
  readonly titleFontSize?: SheetInches;
  readonly titleAlignment?: TextAlignment;
  readonly columnConfig: Table<DataTableColumnConfig>;
  readonly findAndReplace: Table<DataTableFindAndReplaceRow>;
  readonly sortColumnId?: string;
  readonly sortColumnIdOrder?: DataTableColumnSortOrder;
  readonly firstRow?: number;
  readonly maxRowCount?: number;
}

export const DATA_TABLE_SYMBOL_COLUMN_ID = "symbol";

export const DEFAULT_DATA_TABLE_COLUMN_CONFIG: Partial<DataTableColumnConfig> = {
  isHidden: false,
  includeFilter: undefined,
  excludeFilter: undefined,
  width: undefined,
};
